import { Stethoscope, User } from "lucide-react";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { LoadingScreen } from "@/components/ProtectedRoute";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { LOGO_URL } from "@/lib/branding";

export default function CompleteProfile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("patient");
  const [prof, setProf] = useState({ titulo: "Dr.", especialidad: "", cedula: "" });
  const [loading, setLoading] = useState(false);

  if (user === undefined) return <LoadingScreen />;
  if (!user) return <Navigate to="/auth" replace />;
  if (user.role) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = { role };
      if (role === "professional") {
        body.professional_profile = { ...prof, universidad: "", telefono: "", direccion: "" };
      }
      const { data } = await api.post("/auth/complete-profile", body);
      setUser(data);
      toast.success("Perfil completado");
      navigate("/dashboard");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-12">
      <img src={LOGO_URL} alt="TransSalud" className="h-12 w-12 object-contain mb-6" />
      <h1 className="text-2xl sm:text-3xl font-bold mb-2">¡Hola, {user.name?.split(" ")[0]}!</h1>
      <p className="text-muted-foreground mb-8">Cuéntanos cómo usarás la plataforma.</p>

      <form onSubmit={submit} className="w-full max-w-md bg-white border rounded-2xl p-6 sm:p-8 space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setRole("patient")}
            data-testid="complete-role-patient"
            className={`rounded-2xl border p-5 text-center transition-colors duration-200 ${
              role === "patient" ? "border-primary bg-accent" : "hover:bg-secondary"
            }`}
          >
            <User className="h-6 w-6 mx-auto mb-2 text-primary" strokeWidth={1.5} />
            <p className="font-semibold text-sm">Soy paciente</p>
          </button>
          <button
            type="button"
            onClick={() => setRole("professional")}
            data-testid="complete-role-professional"
            className={`rounded-2xl border p-5 text-center transition-colors duration-200 ${
              role === "professional" ? "border-primary bg-accent" : "hover:bg-secondary"
            }`}
          >
            <Stethoscope className="h-6 w-6 mx-auto mb-2 text-primary" strokeWidth={1.5} />
            <p className="font-semibold text-sm">Soy profesional</p>
          </button>
        </div>

        {role === "professional" && (
          <div className="rounded-xl bg-secondary p-4 space-y-3">
            <p className="text-xs text-muted-foreground">
              Tu cuenta será revisada por un administrador antes de activarse.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Título</Label>
                <Input value={prof.titulo} onChange={(e) => setProf({ ...prof, titulo: e.target.value })} data-testid="complete-titulo-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Cédula</Label>
                <Input value={prof.cedula} onChange={(e) => setProf({ ...prof, cedula: e.target.value })} data-testid="complete-cedula-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Especialidad</Label>
              <Input
                value={prof.especialidad}
                onChange={(e) => setProf({ ...prof, especialidad: e.target.value })}
                placeholder="Esp. Salud Trans"
                data-testid="complete-especialidad-input"
              />
            </div>
          </div>
        )}

        <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="complete-profile-submit">
          {loading ? "Guardando..." : "Continuar"}
        </Button>
      </form>
    </div>
  );
}
